// @givmo/app-admin — auth-screen error surfacing (07-1.1a).
//
// Every auth screen handles failures the same way: normalise the thrown value through
// `AuthError.from(e)` and branch on `err.kind` (NEVER on a raw Firebase string). A user-initiated
// cancel (`kind === 'cancelled'`) has an empty message and must surface NOTHING. App-Check failures
// arrive as an api-client `GivmoError` thrown out of a `sdk.*` call → `AuthError.from` wraps it as
// `unknown` and RETAINS the PII-safe server message, so they are shown (no silent retry). PII-safe:
// we render only the AuthError's user message; we never log credentials, emails, or tokens.
//
// EP-SUITE2-FIX6 ⚠ A DOOR'S ERROR LIVES ABOVE THE ROUTER TREE, AT MODULE SCOPE — the join store's
// shape (`sign-up-join-context.tsx`). A door's ceremony signs an account IN before it can fail: the
// provider sign-UP's existing-account arm signs it in and back out ("User is already registered.
// Please sign in."), a provider sign-IN with a new account signs it in and deletes it ("User is not
// found. Please sign up."), a failed email registration deletes the account it created, and a sign-in
// can find no company after it succeeded. Each uid change is the identity boundary's hard cache
// boundary: `app.tsx` remounts the WHOLE `RouterProvider` (`key={cacheEpoch}`), so the screen instance
// whose handler catches the failure has been UNMOUNTED by then, and an error held in its state never
// rendered — the click appeared to do nothing (reproduced in a real browser against the Auth emulator,
// cut employer-portal-12). A door therefore passes its key, and its error is held here, where the
// remounted instance reads it. Its lifetime is the VISIT to that door: the router clears every entry
// when it navigates to a different location (`createAdminRouter`), never on the boundary's own reloads
// of the one it is at — exactly when a ceremony's error arrives — and a new attempt clears it.
// Screens whose actions change no identity keep the error in their own state, as before.

import { useCallback, useState, useSyncExternalStore } from 'react';
import { AuthError } from '@givmo/auth';

/**
 * The doors whose ceremonies cross the identity boundary. A TOTP prompt uses its host door's.
 * `invite` and `invite-email` are the company console's invite page (EP-MNT17), one per error spot:
 * its providers' line under their buttons, and its form's above its button. Its account step creates
 * an account, and a Google or Microsoft account at another address is undone there, which remounts
 * the routes too.
 */
export type AuthDoorKey =
  | 'login-home'
  | 'sign-in'
  | 'sign-up'
  | 'sign-up-email-password'
  | 'invite'
  | 'invite-email';

/**
 * A door's error: its message, and whether it was the company's sign-in lock refusing the sign-in
 * (AS-WEB27), which the door shows as the lock notice instead of the red line. One object per write,
 * so the store's snapshot is stable between writes.
 */
interface DoorError {
  readonly message: string;
  readonly lockRefused: boolean;
}

const doorErrors = new Map<AuthDoorKey, DoorError>();
const listeners = new Set<() => void>();

function writeDoorError(door: AuthDoorKey, entry: DoorError | null): void {
  const current = doorErrors.get(door) ?? null;
  if (
    current === entry ||
    (current !== null &&
      entry !== null &&
      current.message === entry.message &&
      current.lockRefused === entry.lockRefused)
  ) {
    return;
  }
  if (entry === null) doorErrors.delete(door);
  else doorErrors.set(door, entry);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Clear every door's error — the router calls this when the visit ends (a new location). */
export function clearAuthDoorErrors(): void {
  if (doorErrors.size === 0) return;
  doorErrors.clear();
  for (const listener of listeners) listener();
}

export interface UseAuthErrorResult {
  /** The current user-facing error message, or `null` when there is nothing to show. */
  error: string | null;
  /**
   * Whether that error was the company's sign-in lock refusing the sign-in (AS-WEB27): the door then
   * shows the lock notice with the way back in (`SignInDoorError`) instead of the red line.
   */
  lockRefused: boolean;
  /** Clear the error (e.g. on a fresh submit). */
  clearError: () => void;
  /**
   * Normalise a thrown value and store its user message — unless it's a cancellation, which surfaces
   * nothing (and clears any prior error). Returns the resolved {@link AuthError} for callers that
   * need to branch further (e.g. the email/UserAlreadyExists redirect).
   */
  surface: (thrown: unknown) => AuthError;
}

/** What a thrown value surfaces as — `null` for a deliberate cancel. */
function entryOf(authError: AuthError): DoorError | null {
  if (authError.kind === 'cancelled') return null; // a deliberate cancel is not an error to surface
  return {
    message: authError.message.length > 0 ? authError.message : AuthError.userMessage('unknown'),
    lockRefused: authError.kind === 'organizationSsoRequired',
  };
}

/**
 * The screen's error slot. Pass the door's key when its action can change the signed-in identity
 * (the header); omit it and the error is the component's own state.
 */
export function useAuthError(door?: AuthDoorKey): UseAuthErrorResult {
  const [localError, setLocalError] = useState<DoorError | null>(null);
  const read = useCallback(
    () => (door === undefined ? null : (doorErrors.get(door) ?? null)),
    [door],
  );
  const doorError = useSyncExternalStore(subscribe, read, read);

  const write = useCallback(
    (entry: DoorError | null) => {
      if (door === undefined) setLocalError(entry);
      else writeDoorError(door, entry);
    },
    [door],
  );

  const clearError = useCallback(() => write(null), [write]);

  const surface = useCallback(
    (thrown: unknown): AuthError => {
      const authError = AuthError.from(thrown);
      write(entryOf(authError));
      return authError;
    },
    [write],
  );

  const entry = door === undefined ? localError : doorError;
  return {
    error: entry?.message ?? null,
    lockRefused: entry?.lockRefused === true,
    clearError,
    surface,
  };
}
